import { TaskAPI } from 'api/tasks';
import { logger } from 'services';
import { negativeUid } from 'utils/uid';
import { moveTaskToPhase } from './mutations';
import { mapTaskChangesToPayload } from './payload';
import { taskCollection } from './task-collection';
import { notifyTaskCreated } from './task-created-signal';
import { taskFunnel } from './telemetry';
/**
 * Provenance for every push in this module.
 *
 * This file is the WRITE layer: each push here carries the result of a change
 * THIS tab just made server-side, so other tabs need it. Reads live in
 * `fetchers.ts` (default `'fetch'` — silent, since a peer fetches its own data)
 * and socket handlers tag `'ws'` (silent, since every tab receives the event
 * independently). Getting this wrong is invisible: an untagged write simply
 * never reaches other tabs. See `PushSource` in @rocketlane/state-db.
 */ const MUTATION_PUSH = {
    source: 'mutation'
};
/** Narrows an untyped API response to `Task[]` before pushing. */ const isTaskArray = (value)=>Array.isArray(value);
/** Narrows an untyped API response to a single `Task` before pushing. */ const isTask = (value)=>typeof value === 'object' && value !== null && typeof value.taskId === 'number';
/**
 * Action helpers for cases that are not a single-field task update:
 * bulk edits, optimistic create, WS sync, dependencies, ratings.
 *
 * Single-field updates / deletes go through `taskCollection.update` /
 * `.delete` directly (or the `useCollectionMutation` hook).
 */ /**
 * Optimistically create a task. Uses a temporary negative id (matching the
 * existing `negativeUid()` convention); the collection swaps it for the
 * server id once `createTask` resolves.
 */ /**
 * Optimistic task field op-update from a UI surface (task drawer sections,
 * pickers). Wraps `taskCollection.update` with funnel telemetry: emits
 * `task.field_update` on apply and `task.field_update.error` if the server
 * rejects and the overlay rolls back. Reflects on whichever page is mounted
 * (plan / list) since both read this collection. Use this instead of a raw
 * `taskCollection.update` for user-driven single-task edits.
 */ export const optimisticTaskFieldUpdate = (taskId, payload, kind = 'field')=>{
    taskFunnel.fieldUpdate({
        taskId,
        fields: Object.keys(payload !== null && payload !== void 0 ? payload : {}),
        kind
    });
    const tx = taskCollection.update(taskId, payload);
    tx.isPersisted.catch(()=>taskFunnel.fieldUpdateError({
            taskId,
            kind
        }));
    return tx;
};
export const createTaskOptimistic = (projectId, draft)=>{
    var _draft_project, _ref;
    var _draft_parentTaskObject;
    const tempTask = {
        ...draft,
        taskId: negativeUid(),
        project: {
            ...(_draft_project = draft.project) !== null && _draft_project !== void 0 ? _draft_project : {},
            projectId
        }
    };
    // Funnel: a parented draft is a subtask add. Optimistic insert reflects on
    // whichever page is mounted (plan / list) since both read this collection.
    const parentTaskId = (_ref = (_draft_parentTaskObject = draft.parentTaskObject) === null || _draft_parentTaskObject === void 0 ? void 0 : _draft_parentTaskObject.taskId) !== null && _ref !== void 0 ? _ref : draft.parentTask;
    if (parentTaskId != null) {
        taskFunnel.subtaskAdd({
            parentTaskId,
            optimistic: true
        });
    }
    return taskCollection.insert(tempTask);
};
/**
 * Bulk update many tasks (known ids) in a single API call, optimistically.
 * All changes roll back together if the bulk call fails.
 *
 * The bulk endpoint returns no bodies; visible state stays optimistic until a
 * WS refetch reconciles it (consistent with the WS=refetch decision).
 */ export const bulkUpdateTasks = (updates, options = {})=>taskCollection.batch((recorder)=>{
        updates.forEach(({ taskId, changes })=>recorder.update(taskId, changes));
    }, async ({ mutations })=>{
        const tasks = mutations.map((m)=>{
            var _m_changes;
            return {
                taskId: m.id,
                ...mapTaskChangesToPayload((_m_changes = m.changes) !== null && _m_changes !== void 0 ? _m_changes : {})
            };
        });
        await TaskAPI.bulkUpdateTask({
            tasks
        }, {
            isAllTask: options.isAllTask
        });
    });
/** Refetch a single task from the server and push it into the collection. */ export const syncTaskFromServer = async (projectId, taskId)=>{
    const task = await TaskAPI.getTask(projectId, taskId);
    taskCollection.push([
        task
    ], MUTATION_PUSH);
};
/** Refetch several tasks and push them in one batch. */ export const syncTasksFromServer = async (projectId, taskIds)=>{
    const tasks = await Promise.all(taskIds.map((taskId)=>TaskAPI.getTask(projectId, taskId)));
    taskCollection.push(tasks, MUTATION_PUSH);
};
/**
 * Imperative persistence wrappers — call the endpoint, mirror the server
 * response into the collection, and RETURN the result. Use these where the
 * caller needs the updated `Task` synchronously (sagas that `yield`, React
 * Query mutationFns, components that read the response) and manages its own
 * optimistic state. Fire-and-forget optimistic mutations should use
 * `taskCollection.update/insert/delete` instead.
 */ export const createTaskRecord = async (...args)=>{
    const task = await TaskAPI.createTask(...args);
    taskCollection.push([
        task
    ], MUTATION_PUSH);
    return task;
};
export const updateTaskRecord = async (...args)=>{
    const task = await TaskAPI.updateTask(...args);
    taskCollection.push([
        task
    ], MUTATION_PUSH);
    return task;
};
export const updateTaskMiniRecord = async (...args)=>{
    const task = await TaskAPI.updateTaskMini(...args);
    taskCollection.push([
        task
    ], MUTATION_PUSH);
    return task;
};
/**
 * Optimistic + returns the entity (pattern P6). Delegates to the declared
 * `task.moveToPhase` mutation, which owns the overlay, the swimlane
 * `entityOrder` patch and its undo, and the lane-refresh signals peers need.
 */ export const updatePhaseRecord = async (taskId, payload)=>{
    if (taskCollection.get(taskId) === undefined) {
        // Not in the collection — no overlay base, so there is nothing to be
        // optimistic about and no local surface to keep in step. Direct write.
        const task = await TaskAPI.updatePhase(taskId, payload);
        taskCollection.push([
            task
        ], MUTATION_PUSH);
        return task;
    }
    return moveTaskToPhase.run({
        taskId,
        payload
    });
};
export const updatePrivacyRecord = async (...args)=>{
    const task = await TaskAPI.updatePrivacy(...args);
    taskCollection.push([
        task
    ], MUTATION_PUSH);
    return task;
};
export const updateSubTaskOrderRecord = (...args)=>TaskAPI.updateSubTaskOrder(...args);
/**
 * Imperative bulk update — forwards the full `BulkTaskPayload` (including
 * `operationName` / `bulkShiftDates`) to the endpoint. Use where the caller
 * manages its own optimistic state; for AgGrid-style optimistic bulk edits use
 * `bulkUpdateTasks` (which applies an optimistic overlay) instead.
 */ export const bulkUpdateTasksRecord = (...args)=>TaskAPI.bulkUpdateTask(...args);
/** Delete a task on the server and remove it from the collection. */ export const deleteTaskRecord = async (...args)=>{
    const [, taskId] = args;
    await TaskAPI.deleteTask(...args);
    // `source: 'mutation'` — the default is `'ws'`, which the sync adapter
    // deliberately ignores because every tab receives a socket event itself. This
    // is a LOCAL deletion, so a peer without that subscription would keep showing
    // the task.
    taskCollection.externalDelete(taskId, {
        source: 'mutation'
    });
};
/**
 * Submit a CSAT rating. Not a task-field mutation (the endpoint returns a
 * boolean), so it runs outside the optimistic system; refetch reconciles the
 * task's `csatRatings`.
 */ export const submitTaskRating = async (taskId, data, projectId)=>{
    var _taskCollection_get_project, _taskCollection_get;
    await TaskAPI.addRating(taskId, data);
    const resolvedProjectId = projectId !== null && projectId !== void 0 ? projectId : (_taskCollection_get = taskCollection.get(taskId)) === null || _taskCollection_get === void 0 ? void 0 : (_taskCollection_get_project = _taskCollection_get.project) === null || _taskCollection_get_project === void 0 ? void 0 : _taskCollection_get_project.projectId;
    if (resolvedProjectId != null) {
        await syncTaskFromServer(resolvedProjectId, taskId);
    }
};
/**
 * Duplicate a task N times via the dedicated endpoint. Returns no bodies
 * (`Promise<void>`); the resulting tasks arrive via a WS refetch, so there is
 * nothing to push here.
 */ export const duplicateTask = (...args)=>TaskAPI.duplicateTask(...args);
/**
 * The server builds the create response before the task→sprint mapping is
 * written (an `@AfterReturning` AOP action does that), so it never comes back
 * carrying `currentSprint` even when the request asked for one. Restore it
 * from the request payload, here — where the payload and the response meet —
 * so the collection and the created-signal both see the sprint the caller
 * asked for. `sprintName` is not in the payload and stays absent; every
 * consumer optional-chains it.
 */ const withRequestedSprint = (task, requestedSprint)=>!task.currentSprint && (requestedSprint === null || requestedSprint === void 0 ? void 0 : requestedSprint.sprintId) ? {
        ...task,
        currentSprint: requestedSprint
    } : task;
/** Create a task from a form submission → push the returned task. */ export const createTaskWithForm = async (...args)=>{
    var _args__data, _args_;
    const task = withRequestedSprint(await TaskAPI.createTaskWithForm(...args), (_args_ = args[0]) === null || _args_ === void 0 ? void 0 : (_args__data = _args_.data) === null || _args__data === void 0 ? void 0 : _args__data.currentSprint);
    taskCollection.push([
        task
    ], MUTATION_PUSH);
    notifyTaskCreated(task);
    return task;
};
/** Idempotent create (X-Request-Id) → push the returned task. */ export const createTaskWithRequestId = async (...args)=>{
    var _args_;
    const task = withRequestedSprint(await TaskAPI.createTaskWithRequestId(...args), (_args_ = args[1]) === null || _args_ === void 0 ? void 0 : _args_.currentSprint);
    taskCollection.push([
        task
    ], MUTATION_PUSH);
    notifyTaskCreated(task);
    return task;
};
/** Add tasks to a project from templates → push the returned tasks. */ export const addTaskTemplatesToProject = async (...args)=>{
    const result = await TaskAPI.addTaskTemplatesToProject(...args);
    taskCollection.push(result, MUTATION_PUSH);
    return result;
};
/**
 * Bulk-create top-level tasks under a project. The endpoint return is untyped
 * (`Promise<unknown>`); push only when it resolves to a `Task[]`.
 */ export const addMultipleTasks = async (...args)=>{
    const result = await TaskAPI.addMultipleTasks(...args);
    if (isTaskArray(result)) {
        taskCollection.push(result, MUTATION_PUSH);
    }
    return result;
};
/**
 * Bulk-create subtasks under a parent task. The endpoint return is untyped
 * (`Promise<unknown>`); push only when it resolves to a `Task[]`.
 */ export const addMultipleSubtasks = async (...args)=>{
    const [parentTaskId] = args;
    try {
        const result = await TaskAPI.addMultipleSubtasks(...args);
        if (isTaskArray(result)) {
            taskCollection.push(result, MUTATION_PUSH);
        }
        if (typeof parentTaskId === 'number') {
            taskFunnel.subtaskAdd({
                parentTaskId,
                optimistic: false
            });
        }
        return result;
    } catch (error) {
        if (typeof parentTaskId === 'number') {
            taskFunnel.subtaskAddError({
                parentTaskId
            });
        }
        throw error;
    }
};
/**
 * Merge only the `dependentTasks` relation off a freshly-fetched task into
 * whatever's currently cached — never the whole snapshot. Both
 * `createDependentTask` and `updateDependentTasks` fetch the task server-side
 * (`getTaskDetails`) *before* their `@AfterReturning` AOP date-shift cascade
 * runs, so the response always carries pre-cascade dates. A full-task push
 * (`push()` always replaces — `isNewer` is unconditionally `true`) would
 * clobber whatever dates the client just computed optimistically for the
 * same task, which is exactly the "dates revert a moment after the
 * dependency is created" bug this avoids.
 */ const mergeDependentTasksIntoCollection = (fresh)=>{
    const current = taskCollection.get(fresh.taskId);
    if (!current) {
        return;
    }
    taskCollection.push([
        {
            ...current,
            dependentTasks: fresh.dependentTasks
        }
    ], MUTATION_PUSH);
};
/**
 * Create a dependency between tasks. The `add-dependency` endpoint actually
 * returns an echo of the request body (`TaskToDependentTaskModel`), not a
 * `Task` — `TaskAPI.createDependentTask`'s declared return type is a lie.
 * Refetch the real task to reconcile `dependentTasks` (see
 * `mergeDependentTasksIntoCollection` for why only that field is merged).
 *
 * The refetch is best-effort: the dependency was already created server-side
 * by the call above, and callers (the gantt drag saga has no try/catch; the
 * task-pane caller has no `.catch`) would otherwise have an uncaught
 * rejection abort their whole follow-up flow — or, worse, crash the saga
 * middleware — over a reconciliation fetch that isn't on the critical path.
 */ export const createDependentTask = async (...args)=>{
    const [projectId, taskId] = args;
    const result = await TaskAPI.createDependentTask(...args);
    try {
        const fresh = await TaskAPI.getTask(projectId, taskId);
        mergeDependentTasksIntoCollection(fresh);
    } catch (error) {
        logger.error('createDependentTask: failed to refetch task', {
            error,
            projectId,
            taskId
        });
    }
    return result;
};
/**
 * Remove a dependency between tasks. Returns a boolean; callers should refetch
 * the affected task(s) via `syncTasksFromServer` to reconcile state.
 */ export const removeDependentTask = (...args)=>TaskAPI.removeDependentTask(...args);
/**
 * Update a task's dependent tasks. Returns the updated `Task`, which — per
 * `mergeDependentTasksIntoCollection` — only has its `dependentTasks` merged
 * into the collection; the returned dates are pre-cascade and must not
 * overwrite the client-computed optimistic dates. Callers should still
 * refetch the other affected task(s) via `syncTasksFromServer` since the
 * response only carries the directly-edited task.
 */ export const updateDependentTasks = async (...args)=>{
    const result = await TaskAPI.updateDependentTasks(...args);
    mergeDependentTasksIntoCollection(result);
    return result;
};
/**
 * Promote a subtask back to a top-level task. The endpoint return is untyped
 * (`Promise<unknown>`); push only when it resolves to a `Task`.
 */ export const removeAsSubtask = async (...args)=>{
    const result = await TaskAPI.removeAsSubtask(...args);
    if (isTask(result)) {
        taskCollection.push([
            result
        ], MUTATION_PUSH);
    }
    return result;
};
/** Delete a task attachment. No `Task` entity returned — just forward. */ export const deleteTaskAttachment = (...args)=>TaskAPI.deleteTaskAttachment(...args);
/**
 * Bulk-update tasks for a select-all operation. The endpoint returns no per-id
 * bodies, so there is nothing to push; visible state stays optimistic until a
 * WS refetch reconciles it. Just forward.
 */ export const bulkUpdateAllTasks = (...args)=>TaskAPI.bulkUpdateAllTasks(...args);
/**
 * Delete tasks for a select-all operation via the filter-scoped endpoint. The
 * server resolves the full matching set (loaded + unloaded pages) and returns
 * the deleted top-level ids; we remove those from the collection so any
 * collection-driven view reflects the deletion immediately, and the grid is
 * reconciled by the following WS refetch. Fixes the AG-Grid ~300-row
 * cached-selection cap on the client.
 */ export const bulkDeleteAllTasks = async (...args)=>{
    const response = await TaskAPI.bulkDeleteAllTasks(...args);
    const deletedIds = response === null || response === void 0 ? void 0 : response.taskIds;
    (deletedIds !== null && deletedIds !== void 0 ? deletedIds : []).forEach((taskId)=>{
        // Local deletion — see `deleteTaskRecord`.
        taskCollection.externalDelete(taskId, {
            source: 'mutation'
        });
    });
};
/**
 * Apply a dependency date cascade to the tasks this tab has loaded.
 *
 * The server owns the cascade: a date edit sends ONLY the edited task's dates
 * (see `handleChange` in the dependency-conflict modal), and the backend
 * shifts everything downstream. `getDateShiftsForEntity` computes the same
 * shifts client-side purely to render the confirmation table — the numbers
 * were then thrown away, so dependents already on screen kept their old dates
 * until something happened to refetch them.
 *
 * Those numbers are good enough to show the user in a confirmation dialog, so
 * they are good enough to display. Write them.
 *
 * Deliberately scoped to loaded tasks. A dependent on a page this tab has
 * never fetched needs nothing: it arrives already shifted whenever its page
 * loads, because the server got there first.
 *
 * `source: 'local'` — this is a client-computed prediction of what the server
 * is doing, not a confirmed result. Other tabs derive their own from the
 * socket events for the same change; broadcasting ours would hand them a guess
 * they cannot retract if the parent write fails.
 *
 * Returns an undo, so the caller can tie the cascade to the parent
 * transaction: if the edit that triggered it is rejected, the shift never
 * happened either.
 */ export const applyDependentDateShifts = (updates, excludeTaskId)=>{
    const before = [];
    const after = [];
    Object.entries(updates !== null && updates !== void 0 ? updates : {}).forEach(([key, update])=>{
        var _ref, _ref1;
        const taskId = Number(key);
        if (!Number.isFinite(taskId) || taskId === excludeTaskId) {
            return;
        }
        const existing = taskCollection.get(taskId);
        if (existing === undefined) {
            return;
        }
        const startDate = (_ref = update === null || update === void 0 ? void 0 : update.startDate) !== null && _ref !== void 0 ? _ref : existing.startDate;
        const dueDate = (_ref1 = update === null || update === void 0 ? void 0 : update.dueDate) !== null && _ref1 !== void 0 ? _ref1 : existing.dueDate;
        if (startDate === existing.startDate && dueDate === existing.dueDate) {
            return;
        }
        before.push(existing);
        after.push({
            ...existing,
            startDate,
            dueDate
        });
    });
    if (after.length === 0) {
        return ()=>undefined;
    }
    taskCollection.push(after, {
        source: 'local'
    });
    return ()=>{
        taskCollection.push(before, {
            source: 'local'
        });
    };
};
